import { colors, Divider, Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthGuard, ErrorMessage, GenericHeader, LoadingIndicator, Page, RenderGuard, ActivityList } from "components";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from "react-router-dom";
import { Lesson } from "services";
import { ModelsActions } from "store/actions";
import { curriculum_manager } from 'utils/constants';
import useErrorCatcher from "utils/useErrorCatcher";
import useRouter from 'utils/useRouter';
import Summary from "./Summary";
import LessonSlots from "./LessonSlots";
import PayOptionList from "../../../components/PayOptionList";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
  divider: {
    backgroundColor: colors.grey[300]
  },
  content: {
    marginTop: theme.spacing(3)
  }
}));

let mounted = false;

const LessonDetail = props => {
  const { match, history } = props;
  const classes = useStyles();
  const { lesson_id, tab } = match.params;
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.lesson);
  const dispatch = useDispatch();
  const router = useRouter();

  const reload = () => {
    setLoading(true);
    runAsyncBlock(async () => {
      const { model } = await Lesson.detail(lesson_id);
      dispatch(ModelsActions.update_model({ lesson: model }));
    }).finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [lesson_id]);

  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    }
  }, []);

  const handleTabsChange = (event, value) => {
    history.push(value);
  };

  const tabs = [
    { value: "summary", label: "Summary" },
    { value: "lesson_slots", label: "Lesson Slots" },
    { value: "activities", label: "Activities" },
    { value: "payment-options", label: "Payment Options" },
  ];

  if (!tab) {
    return <Redirect to={`/lessons/${lesson_id}/summary`} />;
  }

  if (!tabs.find(t => t.value === tab)) {
    return <Redirect to="/errors/error-404" />;
  }

  const handleDelete = () => {
    setLoading(true);
    runAsyncBlock(async () => {
      await Lesson.del(lesson_id);
      router.history.push('/lessons');
    }).finally(() => mounted && setLoading(false));
  }

  return (
    <AuthGuard roles={[curriculum_manager]} projects={[]}>
      <Page className={classes.root} title="Lesson Details" >
        <ErrorMessage message={errorMessage} />
        <LoadingIndicator variant="card" active={loading} />
        <RenderGuard renderIf={!loading && model}>
          {model && <GenericHeader subheader={model.name_en} header="Lesson" subtitle={model.reference} />}
          <Tabs
            className={classes.tabs}
            onChange={handleTabsChange}
            scrollButtons="auto"
            value={tab}
            variant="scrollable" >
            {tabs.map(tab => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
          </Tabs>
          <Divider className={classes.divider} />
          <div className={classes.content}>
            {tab === "summary" && <Summary handleDelete={handleDelete} reload={reload} />}
            {tab === "lesson_slots" && <LessonSlots reload={reload} />}
            {tab === "activities" && model && <ActivityList lesson_id={model.id} />}
            {tab === "payment-options" && model && <PayOptionList lesson_id={model.id} />}
          </div>
        </RenderGuard>
      </Page>
    </AuthGuard>
  );
};

LessonDetail.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default LessonDetail;
